import type { FeatureId } from "@/lib/client-profile";
/**
 * Tipos do menu e o guarda de tipo. Separados dos dados (`menu-sections.ts`) e
 * da resolução caminho → módulo (`menu-paths.ts`): cada ficheiro tem uma só razão para mudar.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type MenuAccess =
  | "universal"                          // any authenticated user
  | "admin"                              // isAdmin (superuser OR system dept)
  | "superuser"                          // is_superuser only
  | "representative"                     // HR users (users.READ) OR department representatives
  | { entity: string; action: string };  // specific CRUD permission

export interface MenuPath {
  path: string;
  label: string;
  icon?: string;
  access?: MenuAccess; // default: "universal"
  /**
   * Feature-flag do perfil de cliente (ex.: "hr.time_bank"). Se presente, o item
   * só aparece quando o perfil tem essa feature ligada. Ausente = sempre visível
   * (sujeito só a `access`). Ver client-profile.ts / client_profile.py.
   */
  feature?: FeatureId;
  /**
   * Sobrepõe o módulo da secção só para este item (ex.: um item de
   * `comercial_avancado` dentro da secção "comercial", visualmente junto mas
   * com gate próprio — ver ADR-0006). Ausente = usa o módulo da secção.
   */
  module?: string;
  /**
   * O item existe se o perfil tiver QUALQUER um destes módulos (o `module` só
   * aceita um). É o caso do Calendário, que vive no núcleo mas cujos dados
   * vêm de RH, Segurança ou Receção — sem nenhum deles a API responde 404.
   * Ausente = sem esta regra.
   */
  anyModule?: string[];
}

export interface MenuSubGroup {
  groupLabel: string;
  items: MenuPath[];
}

export interface MenuSection {
  id: string;
  title: string;
  items: Array<MenuPath | MenuSubGroup>;
  /**
   * Ligações directas, sem cabeçalho colapsável nem "pai": os itens aparecem
   * soltos no topo da sidebar, mesmo colapsada. Usado pelo topo (Painel,
   * Calendário, Tarefas, Feed), que é do núcleo.
   */
  standalone?: boolean;
}

/** Type guard */
export function isMenuSubGroup(item: MenuPath | MenuSubGroup): item is MenuSubGroup {
  return "groupLabel" in item;
}
